// src/prototype/utils/personalizationProgress.ts
// Shared Personalize progress for org profile, surveys, and document uploads.

import SurveyService from '@/services/surveyService';
import { getClientProfile } from '@/services/clientProfileService';
import { calculateProfileStrength } from '@/utils/calculateProfileStrength';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8080';

export const PERSONALIZATION_TAB_MIN_PERCENT = 80;

export interface CorePersonalizationProgress {
  profile: number;
  surveys: number;
  documents: number;
}

export interface PersonalizationProgressWithInitiatives extends CorePersonalizationProgress {
  initiatives?: number;
}

/** Equal-weight average across profile, surveys, documents, and initiatives. */
export function calculateOverallPersonalizationStrength(
  data: PersonalizationProgressWithInitiatives,
): number {
  const profile = data.profile || 0;
  const surveys = data.surveys || 0;
  const documents = data.documents || 0;
  const initiatives = data.initiatives || 0;
  return Math.round((profile + surveys + documents + initiatives) / 4);
}

/** Same formula everywhere: 20% per selected finalist, capped at 100. */
export function initiativesScoreFromFinalistCount(count: number): number {
  if (!Number.isFinite(count) || count <= 0) return 0;
  return Math.min(100, Math.round(count * 20));
}

export function hasCorePersonalizationComplete(
  progress: CorePersonalizationProgress,
  minPercent: number = PERSONALIZATION_TAB_MIN_PERCENT,
): boolean {
  return (
    progress.profile > minPercent &&
    progress.surveys > minPercent &&
    progress.documents > minPercent
  );
}

export function getPersonalizationBlockers(
  progress: CorePersonalizationProgress,
  minPercent: number = PERSONALIZATION_TAB_MIN_PERCENT,
): string[] {
  const blockers: string[] = [];
  if (progress.profile <= minPercent) blockers.push('Organization Profile');
  if (progress.surveys <= minPercent) blockers.push('Surveys');
  if (progress.documents <= minPercent) blockers.push('Uploads');
  return blockers;
}

/**
 * Organization Profile % — MUST match Client Profile → Profile Strength sidebar.
 *
 * Uses only calculateProfileStrength() on the normalized profile payload.
 * Do NOT fall back to /readiness (different metric) or /strength API (can diverge);
 * that caused Personalize to show 67% while Profile Strength showed 74%.
 */
async function fetchProfileProgress(clientId: string): Promise<number> {
  try {
    const profile = await getClientProfile(clientId);
    const { score } = calculateProfileStrength(profile);
    if (!Number.isFinite(score)) return 0;
    return Math.max(0, Math.min(100, Math.round(score)));
  } catch (error) {
    console.error('[EA] Profile progress error:', error);
    return 0;
  }
}

async function fetchSurveyProgress(clientId: string): Promise<number> {
  try {
    const result = await SurveyService.getAllProgress(clientId, true);

    let totalAnswered = 0;
    let totalQuestions = 0;

    if (result.strategy) {
      totalAnswered += result.strategy.answered ?? 0;
      totalQuestions += result.strategy.total || 9;
    } else {
      totalQuestions += 9;
    }

    if (result.capabilities) {
      totalAnswered += result.capabilities.answered ?? 0;
      totalQuestions += result.capabilities.total || 14;
    } else {
      totalQuestions += 14;
    }

    if (result.readiness) {
      totalAnswered += result.readiness.answered ?? 0;
      totalQuestions += result.readiness.total || 14;
    } else {
      totalQuestions += 14;
    }

    return totalQuestions > 0 ? Math.round((totalAnswered / totalQuestions) * 100) : 0;
  } catch (error) {
    console.error('[EA] Survey progress error:', error);
    return 0;
  }
}

const documentProgressCache = new Map<string, { at: number; value: number }>();
const DOCUMENT_PROGRESS_TTL_MS = 60_000;

async function fetchDocumentProgress(clientId: string): Promise<number> {
  const cached = documentProgressCache.get(clientId);
  if (cached && Date.now() - cached.at < DOCUMENT_PROGRESS_TTL_MS) return cached.value;

  try {
    const token = localStorage.getItem('dva_token');
    const response = await fetch(
      `${API_BASE}/api/v2/documents?clientId=${encodeURIComponent(clientId)}&document_kind=strategy`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        credentials: 'include',
      },
    );

    if (!response.ok) return 0;

    const data = await response.json();
    // Match DocumentManagementPage parsing order
    const documents = data.data || data.documents || [];
    const docCount = Array.isArray(documents) ? documents.length : 0;

    const value = docCount >= 3 ? 100 : docCount === 2 ? 66 : docCount === 1 ? 33 : 0;
    documentProgressCache.set(clientId, { at: Date.now(), value });
    return value;
  } catch (error) {
    console.error('[EA] Document progress error:', error);
    return 0;
  }
}

export async function loadCorePersonalizationProgress(
  clientId: string,
): Promise<CorePersonalizationProgress> {
  const [profile, surveys, documents] = await Promise.all([
    fetchProfileProgress(clientId),
    fetchSurveyProgress(clientId),
    fetchDocumentProgress(clientId),
  ]);

  return { profile, surveys, documents };
}
